import { Drawer as AntDrawer } from "antd";
import { clsx } from "clsx";
import { CSSProperties, FC, ReactNode } from "react";
import { zIndex } from "../../styles/tokens";
import { IconButton } from "../IconButton";
import { Heading } from "../Typography";
import "./MobileSheet.css";

/**
 * Edge the sheet slides in from.
 *
 * - `bottom` — the default. Content the user acts on and dismisses (filters,
 *   an action list). Reachable with one thumb.
 * - `left` — app navigation, matching the side the desktop navbar rail is on.
 * - `right` — contextual detail, matching the side the desktop drawers open on.
 */
export type MobileSheetPlacement = "bottom" | "left" | "right";

export interface MobileSheetProps {
  /** Whether the sheet is open. */
  open: boolean;

  /** Called when the sheet is dismissed (close button, backdrop, or Esc). */
  onClose: () => void;

  /**
   * Heading shown in the sheet's header row. Omit for a sheet whose content
   * is self-describing (e.g. a nav list) — `ariaLabel` then names it for
   * assistive technology.
   */
  title?: ReactNode;

  /**
   * Accessible name for the dialog. Required when `title` is omitted or is
   * not plain text, so the sheet is never announced as an unnamed dialog.
   */
  ariaLabel?: string;

  /** @default "bottom" */
  placement?: MobileSheetPlacement;

  /**
   * Maximum height of a `bottom` sheet, as a percentage of the viewport.
   *
   * The sheet is as tall as its content and grows only up to this — a filter
   * set of five controls should not open a full-screen panel with the apply
   * button stranded at the bottom of a field of nothing. Ignored for side
   * placements, which are always full height.
   * @default 85
   */
  heightPercent?: number;

  /**
   * Width of a `left` / `right` sheet as a percentage of the viewport.
   * Ignored for `bottom`. Capped so the backdrop stays tappable — a sheet
   * covering the full width gives the user no way out but the close button.
   * @default 82
   */
  widthPercent?: number;

  /** Sheet body. */
  children: ReactNode;

  /**
   * Pinned to the bottom of the sheet, outside the scrolling body — for the
   * action that commits what the body collects (e.g. "Show 42 results").
   */
  footer?: ReactNode;

  /** @default true */
  showCloseButton?: boolean;

  /**
   * Inset the body from the sheet's edges.
   *
   * Turn it off for content that paints its own full-bleed surface — a
   * sidebar, say — where the inset shows as strips of the sheet's own
   * background down both sides.
   * @default true
   */
  padded?: boolean;

  /** Additional class on the sheet surface. */
  className?: string;

  dataTestId?: string;
}

/** Sheets sit on Ant's popup base tier, below dropdowns opened from inside. */
const SHEET_Z_INDEX = zIndex.antPopupBase;

/**
 * A light dismissible sheet for touch layouts.
 *
 * The design system's `Drawer` is the desktop workspace drawer — it is
 * resizable, has a minimum width of several hundred pixels, and carries a
 * header/footer contract built for reviewing a record. None of that fits a
 * phone, so this is a separate primitive rather than a `Drawer` variant:
 * one surface, one dismissal, no resize.
 *
 * It is the counterpart to controls that do not fit on a narrow screen —
 * a filter bar, a nav rail. Render it *instead of* the wide layout below the
 * breakpoint, never in addition to it, so there is one copy of each control
 * in the accessibility tree.
 *
 * @example
 * ```tsx
 * <MobileSheet open={open} onClose={close} title="Filters"
 *   footer={<Button size="xl" className="w-full">Show results</Button>}>
 *   <FilterSelector … />
 * </MobileSheet>
 * ```
 */
export const MobileSheet: FC<MobileSheetProps> = ({
  open,
  onClose,
  title,
  ariaLabel,
  placement = "bottom",
  heightPercent = 85,
  widthPercent = 82,
  children,
  footer,
  showCloseButton = true,
  padded = true,
  className,
  dataTestId,
}) => {
  const isBottom = placement === "bottom";

  const accessibleName =
    ariaLabel ?? (typeof title === "string" ? title : undefined);

  return (
    <AntDrawer
      open={open}
      onClose={onClose}
      placement={placement}
      // Ant renders its own header/footer chrome; ours is in the body so the
      // header can carry a design-system IconButton and the drag handle.
      closable={false}
      title={null}
      footer={null}
      // `auto` + a max on the content (see MobileSheet.css) rather than a
      // fixed percentage: antd sizes the drawer from this value, so a number
      // here is a floor as well as a ceiling.
      height={isBottom ? "auto" : undefined}
      width={isBottom ? undefined : `${widthPercent}%`}
      zIndex={SHEET_Z_INDEX}
      rootClassName={clsx(
        "indigo-mobile-sheet",
        `indigo-mobile-sheet--${placement}`,
        className,
      )}
      // `dvh`, not `%`: the cap is read on the content as well as the
      // wrapper, and a percentage there would be of the wrapper's `auto`
      // height — i.e. nothing. See MobileSheet.css.
      style={
        isBottom
          ? ({
              "--indigo-mobile-sheet-max-height": `${heightPercent}dvh`,
            } as CSSProperties)
          : undefined
      }
      // The body is a flex column so `footer` can pin while the content
      // between the header and it scrolls.
      styles={{ body: { padding: 0 } }}
      aria-label={accessibleName}
      data-testid={dataTestId}
    >
      <div className="indigo-mobile-sheet__surface">
        {isBottom && (
          // Affordance only — the sheet is not drag-dismissable, so it must
          // not be in the tab order or announced as a control.
          <div className="indigo-mobile-sheet__grabber" aria-hidden="true" />
        )}

        {(title || showCloseButton) && (
          <div className="indigo-mobile-sheet__header">
            {title ? (
              <Heading level={3} className="indigo-mobile-sheet__title">
                {title}
              </Heading>
            ) : (
              <span />
            )}
            {showCloseButton && (
              <IconButton
                iconName="close"
                size="md"
                variant="ghost"
                ariaLabel="Close"
                onClick={onClose}
                data-testid={
                  dataTestId ? `${dataTestId}-close` : "MobileSheet-close"
                }
              />
            )}
          </div>
        )}

        <div
          className={clsx(
            "indigo-mobile-sheet__body",
            !padded && "indigo-mobile-sheet__body--flush",
          )}
        >
          {children}
        </div>

        {footer && <div className="indigo-mobile-sheet__footer">{footer}</div>}
      </div>
    </AntDrawer>
  );
};

export default MobileSheet;
